import { useEffect, useState } from "react";
import "./css/SigningFlow.css";

export default function GuardianConsentScreen({
    flowData,
    updateFlowData,
    flowContext,
    updateFlowContext,
    next,
    back,
    loading,
    setLoading,
    error,
    setError,
}) {

    const guardian = flowData?.guardian || {};
    const child = flowData?.child || {};

    const [guardianConsent, setGuardianConsent] = useState(false);
    const [guardianFirstName, setGuardianFirstName] = useState(guardian.firstName || "");
    const [guardianLastName, setGuardianLastName] = useState(guardian.lastName || "");

    useEffect(() => {
        setGuardianFirstName(guardian.firstName || "");
        setGuardianLastName(guardian.lastName || "");
    }, [guardian.firstName, guardian.lastName]);
    
    const childFullName = [
        child.firstName,
        child.lastName
    ].filter(Boolean).join(" ");
   
    const guardianRelationship = guardian.relationship || "";    
    const guardianConsentText = flowData?.guardianConsentMessages?.[guardianRelationship] || "";

    const MIN_GUARDIAN_CONSENT_TEXT_LENGTH = 20;
    if (
        guardianRelationship &&
        (
            guardianRelationship === "PARENT" ||
            guardianRelationship === "LEGAL_GUARDIAN"
        ) &&
        guardianConsentText.length < MIN_GUARDIAN_CONSENT_TEXT_LENGTH
    ) {
        return (
            <div className="page">
                <div className="page-content">
                    <div className="text-red-600 mt-2">
                        Guardian consent message is unavailable or invalid.
                        Please contact support.
                    </div>
                </div>
            </div>
        );
    }
    
    const continueToNext = () => {
        if (loading) return;

        setError("");

        if (!guardianFirstName.trim()) {
            setError("Please enter your first name.");
            return;
        }

        if (!guardianLastName.trim()) {
            setError("Please enter your last name.");
            return;
        }

        if (
            guardianRelationship !== "PARENT" &&
            guardianRelationship !== "LEGAL_GUARDIAN"
        ) {
            setError("Please select your relationship to the child.");
            return;
        }

        if (!guardianConsent) {
            setError(
                "Please confirm that you are the parent or legal guardian and are authorized to provide consent."
            );
            return;
        }

        next({
            action: "continue",
            guardianConsent,
            guardian: {
                firstName: guardianFirstName.trim(),
                lastName: guardianLastName.trim(),
                relationship: guardianRelationship
            }
        });
    };

    return (
        <div className={`page ${loading ? "page-loading" : ""}`}>
            
            {loading && (
                <div className="loading-overlay">
                    Loading...
                </div>
            )}

            {error && (
                <div className="text-red-600 mt-2">
                    {error}
                </div>
            )}
            
            <div className="page-header">

                <h2 className="business-name">
                    {flowContext.selectedService?.name
                        ? `${flowContext.selectedService.name} at ${flowContext.business?.businessName}`
                        : flowContext.business?.businessName}
                </h2>

            </div>

            <div className="page-content">

                <h3 className="form-title">
                    Guardian Consent
                </h3>

                <div className="form-group">

                    <label>
                        Signing for
                    </label>

                    <div className="guardian-signing-card">

                        <div>
                            {childFullName || "Child information unavailable"}
                        </div>

                        <div className="guardian-signing-subtitle">
                            {flowContext.selectedService?.name || "Selected service"}
                            {" at "}
                            {flowContext.business?.businessName || "Business"}
                        </div>

                    </div>

                </div>

                <div className="form-group"> 
                    <label>First Name</label>

                    <input
                        type="text"
                        placeholder="Enter your first name"
                        value={guardianFirstName}
                        onChange={(e) => {
                            const value = e.target.value;
                            setGuardianFirstName(value);

                            updateFlowData({
                                guardian: {
                                    ...guardian,
                                    firstName: value
                                }
                            });
                        }}
                    />
                </div>
                <div className="form-group"> 
                    <label>Last Name</label>

                    <input
                        type="text"
                        placeholder="Enter your last name"
                        value={guardianLastName}
                        onChange={(e) => {
                            const value = e.target.value;
                            setGuardianLastName(value);

                            updateFlowData({
                                guardian: {
                                    ...guardian,
                                    lastName: value
                                }
                            });
                        }}
                    />

                    <div className="guardian-note">
                        Please enter your legal name.
                    </div>
                </div>

               
                <div className="form-group"> 

                    <label> 
                        Relationship to the child 
                    </label> 

                    <div className="relationship-toggle"> 

                        <button 
                            type="button" 
                            className={ 
                                guardianRelationship === "PARENT" 
                                    ? "relationship-option active" 
                                    : "relationship-option" 
                            } 
                            onClick={() => {
                                setGuardianConsent(false);
                                setError("");

                                updateFlowData({
                                    guardian: {
                                        ...guardian,
                                        relationship: "PARENT"
                                    }
                                });
                            }}
                        > 
                            Parent 
                        </button> 

                        <button 
                            type="button" 
                            className={ 
                                guardianRelationship === "LEGAL_GUARDIAN" 
                                    ? "relationship-option active" 
                                    : "relationship-option" 
                            } 
                            onClick={() => {
                                setGuardianConsent(false);
                                setError("");

                                updateFlowData({
                                    guardian: {
                                        ...guardian,
                                        relationship: "LEGAL_GUARDIAN"
                                    }
                                });
                            }}
                        > 
                            Legal Guardian 
                        </button> 

                    </div> 

                </div>

                <div className="guardian-checkbox-card">

                    <input
                        type="checkbox"
                        id="guardianConsentCheck"
                        checked={guardianConsent}
                        onChange={(e) => setGuardianConsent(e.target.checked)}
                    />
                    
                    <label htmlFor="guardianConsentCheck">
                        {guardianConsentText}
                    </label>
    
                    
                    {/* Any changes in below sentence of logic must be updated in backend file: waiver_flow.handlers.helpers.js by searching for the field: flow.guardianConsentText  or guardianConsentText in the model, as it is shown in PDF too.
                     <label htmlFor="guardianConsentCheck">
                        I confirm I am the {guardianRelationshipLabel} of{" "}
                        {childFullName || "the child"} and am authorized to
                        provide consent.
                    </label>*/}
                </div>

            </div>

            <div className="page-footer">

                <button
                    className="primary-button"
                    onClick={continueToNext}
                    disabled={
                        !guardianFirstName.trim() ||
                        !guardianLastName.trim() ||
                        !["PARENT", "LEGAL_GUARDIAN"].includes(guardianRelationship) ||
                        !guardianConsent ||
                        loading
                    }
                >
                    Continue
                    <span className="button-arrow">›</span>
                </button>

                <div className="powered-by">
                    Powered by <strong>DidYouSign</strong>
                </div>

            </div>

        </div>
    );
}